"use client";

import { useLocale } from "next-intl";
import { track } from "@/lib/analytics";
import { EVENEMENT } from "@/lib/evenements";
import { PLAY_STORE_URL } from "@/lib/config";
import { cn } from "@/lib/utils";

/**
 * Badge « Disponible sur Google Play » (lien direct vers la fiche Play Store).
 *
 * Volontairement construit à la main, comme AppStoreBadge : les badges
 * officiels des deux magasins n'ont ni la même hauteur ni les mêmes marges
 * internes, et les poser côte à côte donne un alignement bancal. Ici les deux
 * partagent exactement le même gabarit espresso.
 */
export function PlayStoreBadge({
  className,
  compact = false,
}: {
  className?: string;
  /** Variante réduite, pour le pied de page : elle ne doit pas peser
   *  autant qu'un appel à l'action. */
  compact?: boolean;
}) {
  const locale = useLocale();
  const line1 = locale === "en" ? "Get it on" : "Disponible sur";
  const line2 = "Google Play";

  return (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => track(EVENEMENT.APP_STORE_CLIC, { boutique: "google", emplacement: compact ? "pied" : "page" })}
      aria-label={`${line1} ${line2}`}
      className={cn(
        "inline-flex items-center bg-espresso text-white no-underline transition hover:-translate-y-0.5",
        compact
          ? "gap-2 rounded-[10px] px-3 py-1.5"
          : "gap-2.5 rounded-[13px] px-[18px] py-2.5",
        className,
      )}
    >
      <svg
        width={compact ? 17 : 24}
        height={compact ? 17 : 24}
        viewBox="0 0 24 24"
        fill="#fff"
        aria-hidden
      >
        <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
      </svg>
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span
          className={cn(
            "font-medium opacity-80",
            compact ? "text-[8.5px]" : "text-[10.5px]",
          )}
        >
          {line1}
        </span>
        <span
          className={cn(
            "mt-0.5 font-bold tracking-tight",
            compact ? "text-[12.5px]" : "text-[17px]",
          )}
        >
          {line2}
        </span>
      </span>
    </a>
  );
}
